import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type DuplicateAnalysisMutationError, duplicateAnalysis, getGetAnalysisQueryKey } from "Infrastructure/ApiZfp/AnalyzerApi";
import { DuplicateAnalysisBadRequestErrorCode } from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../AnalysisDetailContext";
import { type SourceVersion, getDuplicateErrorResource } from "./utils";

const DUPLICATE_TOAST_ID = "duplicate-analysis";

type Variables = {
  readonly sourceVersion: SourceVersion;
  readonly confirmConflict: boolean;
};

type Conflict = {
  readonly sourceVersion: SourceVersion;
  readonly existingAnalysis: { readonly analysisId: string; readonly versionId: string } | null;
};

export type DuplicateConflict = {
  readonly sourceVersion: SourceVersion | null;
  readonly canOpenExisting: boolean;
  readonly isConfirming: boolean;
  readonly errorText: string | null;
  readonly confirm: () => void;
  readonly openExisting: () => void;
  readonly cancel: () => void;
};

/**
 * Copies a version: an own analysis gets a new version, a shared one is copied into the caller's own analysis - a new
 * one, or, once the caller confirms the conflict, a new version of the analysis they already have for the same client.
 */
export const useDuplicateVersion = () => {
  const { t } = useResource();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const [conflict, setConflict] = useState<Conflict | null>(null);

  const openVersion = (targetAnalysisId: string, targetVersionId: string) =>
    navigate(`${AppRouting.AnalysisDetail.path(targetAnalysisId, targetVersionId)}${location.search}`);

  const { mutate, isPending, error, variables, reset } = useMutation<
    Awaited<ReturnType<typeof duplicateAnalysis>>,
    DuplicateAnalysisMutationError,
    Variables
  >({
    mutationFn: ({ sourceVersion, confirmConflict }) => duplicateAnalysis({ sourceAnalysisGuid: sourceVersion.guid, confirmConflict }),
    onMutate: ({ confirmConflict }) => {
      // the conflict dialog shows its own progress
      if (!confirmConflict) toast.loading(t(Resources.AnalysisDetail.Duplicate.Duplicating), { id: DUPLICATE_TOAST_ID });
    },
    onSuccess: (result, { confirmConflict }) => {
      setConflict(null);
      queryClient.invalidateQueries({ queryKey: getGetAnalysisQueryKey(analysisId, versionId), refetchType: "none" });
      const isNewVersion = confirmConflict || result.analysisId === analysisId;
      toast.success(
        isNewVersion
          ? t(Resources.AnalysisDetail.Toast.NewVersionCreated, { version: result.version })
          : t(Resources.AnalysisDetail.Duplicate.Duplicated),
        { id: DUPLICATE_TOAST_ID },
      );
      openVersion(result.analysisId, result.versionId);
    },
    onError: (error, { sourceVersion, confirmConflict }) => {
      if (confirmConflict) return;

      if (error?.code === DuplicateAnalysisBadRequestErrorCode.analysis_already_exists_for_client) {
        toast.dismiss(DUPLICATE_TOAST_ID);
        const existingAnalysis = error.analysisId && error.versionId ? { analysisId: error.analysisId, versionId: error.versionId } : null;
        setConflict({ sourceVersion, existingAnalysis });
        return;
      }

      toast.error(t(getDuplicateErrorResource(error?.code) ?? Resources.AnalysisDetail.Toast.ActionError), { id: DUPLICATE_TOAST_ID });
    },
  });

  const closeConflict = () => {
    reset();
    setConflict(null);
  };

  const duplicateConflict: DuplicateConflict = {
    sourceVersion: conflict?.sourceVersion ?? null,
    canOpenExisting: !!conflict?.existingAnalysis,
    isConfirming: isPending,
    errorText:
      error && variables?.confirmConflict ? t(getDuplicateErrorResource(error.code) ?? Resources.AnalysisDetail.Toast.ActionError) : null,
    confirm: () => {
      if (conflict) mutate({ sourceVersion: conflict.sourceVersion, confirmConflict: true });
    },
    openExisting: () => {
      if (!conflict?.existingAnalysis) return;
      closeConflict();
      openVersion(conflict.existingAnalysis.analysisId, conflict.existingAnalysis.versionId);
    },
    cancel: closeConflict,
  };

  return {
    duplicate: (sourceVersion: SourceVersion) => mutate({ sourceVersion, confirmConflict: false }),
    isDuplicating: isPending,
    conflict: duplicateConflict,
  };
};
